/**
 * NotificationPopover Component
 *
 * Dropdown popover showing recent notifications with smooth animations.
 * Shows up to 5 recent notifications with links to view all.
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { BellOff, CheckCheck, ExternalLink } from "lucide-react";
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
} from "@/hooks/use-notifications";
import { NotificationItem } from "./NotificationItem";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

// ═══════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════

interface NotificationPopoverProps {
  onClose?: () => void;
}

export function NotificationPopover({ onClose }: NotificationPopoverProps) {
  const { data, isLoading, error } = useNotifications({ limit: 5 });
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteNotification = useDeleteNotification();

  const notifications = data?.notifications ?? [];
  const total = data?.total ?? 0;

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.readAt).length,
    [notifications]
  );

  const handleMarkAsRead = (id: string) => {
    markAsRead.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const handleDelete = (id: string) => {
    deleteNotification.mutate(id);
  };

  return (
    <div
      className={cn(
        // Responsiv bredd: full på mobil, fast på desktop
        "w-[calc(100vw-2rem)] sm:w-[380px]",
        "max-h-[70vh] sm:max-h-[480px]",
        "bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden",
        "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">Notifikationer</h3>
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-sky-100 text-sky-700">
              {unreadCount} nya
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            disabled={markAllAsRead.isPending}
            className="flex items-center gap-1.5 text-xs font-medium text-sky-600 hover:text-sky-700 disabled:opacity-50 transition-colors"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            Markera alla
          </button>
        )}
      </div>

      {/* Content */}
      <div className="overflow-y-auto max-h-[340px]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="w-6 h-6 text-sky-600" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
              <BellOff className="w-6 h-6 text-red-500" />
            </div>
            <p className="text-sm text-gray-500">
              Kunde inte ladda notifikationer
            </p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center mb-4 shadow-inner">
              <BellOff className="w-7 h-7 text-gray-400" />
            </div>
            <p className="font-medium text-gray-700 mb-1">Inga notifikationer</p>
            <p className="text-sm text-gray-400 max-w-[200px]">
              Du har inga notifikationer just nu. Vi meddelar dig när det händer
              något.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification, index) => (
              <div
                key={notification.id}
                style={{
                  animationDelay: `${index * 50}ms`,
                }}
                className="animate-in fade-in-0 slide-in-from-left-2 duration-300"
              >
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                  compact
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-2.5 bg-gray-50/50">
          <Link
            to="/dashboard/notifications"
            onClick={onClose}
            className="flex items-center justify-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors py-1"
          >
            Visa alla {total > 5 && `(${total})`}
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}
